/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is an event or a reason code; it renders nothing */
// C2 Offline identity, the contract (pure, driven by
// identity_transitions.json). Four facts, never collapsed into one flag:
//
//   offline_owner       persisted (device index), set at sign-in, cleared only by C3
//   session_usable      derived: a token for the owner with exp > now + 60 s
//   refresh_pending     in memory: the last refresh failed transiently
//   terminal_rejection  persisted: GoTrue said the refresh token is dead
//
// src/lib/offline/identity.ts holds the app's copy of the facts.
import {
  ACCOUNT_OWNER_PREFIX, C3_REASON, GUEST_OWNER, IDENTITY_EMIT, MODE, TERMINAL_REFRESH_CODES,
  type C3Reason, type IdentityEmit, type IdentityMode,
} from './offline.codes'

export interface IdentityFacts {
  offlineOwner: string | null
  sessionUsable: boolean
  refreshPending: boolean
  terminalRejection: boolean
}

export interface IdentityAllowed {
  read: boolean
  queue: boolean
  drain: boolean
  edit: boolean
}

export type IdentityEvent =
  /** Process start with the persisted facts: the stored access token's expiry (ms, null: none). */
  | { type: 'cold_start'; tokenExpiryMs: number | null; nowMs: number }
  | { type: 'token_near_expiry' }
  /** The refresh answered: 2xx with the session's subject, or an error status with GoTrue's code. */
  | { type: 'refresh_result'; httpStatus: number; errorCode?: string | null; subject?: string | null }
  | { type: 'sign_in'; subject: string }
  | { type: 'sign_out' }
  | { type: 'user_edit' }

export interface IdentityResult {
  facts: IdentityFacts
  emits: IdentityEmit[]
  /** The C3 reason when `emits` holds run_c3. */
  c3Reason?: C3Reason
}

/** `acct:<uuid lowercase>` for an account. */
export function accountOwner(subject: string): string {
  return `${ACCOUNT_OWNER_PREFIX}${subject.toLowerCase()}`
}

export function isAccountOwner(owner: string | null | undefined): owner is string {
  return typeof owner === 'string' && owner.startsWith(ACCOUNT_OWNER_PREFIX) && owner.length > ACCOUNT_OWNER_PREFIX.length
}

/** The account subject (user id) of an owner key, or null for guest / none. */
export function ownerSubject(owner: string | null | undefined): string | null {
  return isAccountOwner(owner) ? owner.slice(ACCOUNT_OWNER_PREFIX.length) : null
}

export function identityMode(f: IdentityFacts): IdentityMode {
  if (!isAccountOwner(f.offlineOwner)) return MODE.guest
  if (f.terminalRejection) return MODE.rejected
  return f.sessionUsable ? MODE.active : MODE.refreshPending
}

export function allowedIn(mode: IdentityMode): IdentityAllowed {
  switch (mode) {
    case MODE.guest: return { read: true, queue: false, drain: false, edit: true }
    case MODE.active: return { read: true, queue: true, drain: true, edit: true }
    case MODE.refreshPending: return { read: true, queue: true, drain: false, edit: true }
    case MODE.rejected: return { read: true, queue: false, drain: false, edit: false }
  }
}

/** session_usable: a token whose exp is more than 60 s away. */
export function tokenUsable(expiryMs: number | null | undefined, nowMs: number): boolean {
  return typeof expiryMs === 'number' && expiryMs - nowMs > 60_000
}

/** A GoTrue 400 body's code: `error_code`, else the legacy `error`. */
export function refreshErrorCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const o = body as { error_code?: unknown; error?: unknown; code?: unknown }
  if (typeof o.error_code === 'string') return o.error_code
  if (typeof o.code === 'string') return o.code
  if (typeof o.error === 'string') return o.error
  return null
}

export function isTerminalRefresh(status: number, code: string | null | undefined): boolean {
  return status === 400 && !!code && TERMINAL_REFRESH_CODES.has(code)
}

export function applyIdentity(before: IdentityFacts, event: IdentityEvent): IdentityResult {
  let f: IdentityFacts = { ...before }
  const wasMode = identityMode(before)
  const subjectOfOwner = ownerSubject(before.offlineOwner)
  switch (event.type) {
    case 'cold_start': {
      if (!isAccountOwner(f.offlineOwner) || f.terminalRejection) return { facts: f, emits: [] }
      f.sessionUsable = tokenUsable(event.tokenExpiryMs, event.nowMs)
      if (!f.sessionUsable) f.refreshPending = true
      return { facts: f, emits: [] }
    }
    case 'token_near_expiry': {
      if (!isAccountOwner(f.offlineOwner)) return { facts: f, emits: [] }
      f.sessionUsable = false
      return { facts: f, emits: f.terminalRejection ? [] : [IDENTITY_EMIT.requestRefresh] }
    }
    case 'refresh_result': {
      if (!isAccountOwner(f.offlineOwner) || wasMode === MODE.rejected) return { facts: f, emits: [] }
      if (event.httpStatus >= 200 && event.httpStatus < 300) {
        // A token for someone else never becomes the owner's session.
        if (!event.subject || event.subject.toLowerCase() !== subjectOfOwner) return { facts: f, emits: [] }
        f.sessionUsable = true
        f.refreshPending = false
        f.terminalRejection = false
        return { facts: f, emits: wasMode === MODE.active ? [] : [IDENTITY_EMIT.sessionRestored] }
      }
      if (isTerminalRefresh(event.httpStatus, event.errorCode)) {
        f.terminalRejection = true
        f.sessionUsable = false
        f.refreshPending = false
        return { facts: f, emits: [IDENTITY_EMIT.showSignInAgain] }
      }
      // 5xx, 429, a 400 with an unknown code (user_banned included):
      // transient, never a sign-out.
      f.refreshPending = true
      return { facts: f, emits: [] }
    }
    case 'sign_in': {
      const incoming = accountOwner(event.subject)
      if (!isAccountOwner(f.offlineOwner)) {
        // guest -> account: the existing guest -> account migration runs unchanged.
        f = { offlineOwner: incoming, sessionUsable: true, refreshPending: false, terminalRejection: false }
        return { facts: f, emits: [] }
      }
      if (f.offlineOwner === incoming) {
        f.sessionUsable = true
        f.refreshPending = false
        f.terminalRejection = false
        return { facts: f, emits: wasMode === MODE.active ? [] : [IDENTITY_EMIT.sessionRestored] }
      }
      // Another account: C3 for the departing owner runs BEFORE the incoming
      // session is adopted. These are the facts once it did.
      f = { offlineOwner: incoming, sessionUsable: true, refreshPending: false, terminalRejection: false }
      return { facts: f, emits: [IDENTITY_EMIT.runC3], c3Reason: C3_REASON.accountSwitch }
    }
    case 'sign_out': {
      f = { offlineOwner: GUEST_OWNER, sessionUsable: false, refreshPending: false, terminalRejection: false }
      return { facts: f, emits: [IDENTITY_EMIT.runC3], c3Reason: C3_REASON.signOut }
    }
    case 'user_edit':
      return { facts: f, emits: wasMode === MODE.rejected ? [IDENTITY_EMIT.showSignInAgain] : [] }
  }
}
